import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

export default function MembersRanking({
  members = [],
  currentUserId,
  THEME_COLOR,
}) {
  const navigate = useNavigate();

  const topThree = members.slice(0, 3);
  const others = members.slice(3);

  const podiumOrder = useMemo(() => {
    const order = [];
    if (topThree[1]) order.push(topThree[1]);
    if (topThree[0]) order.push(topThree[0]);
    if (topThree[2]) order.push(topThree[2]);
    return order;
  }, [topThree]);

  if (members.length === 0) {
    return (
      <div className="card shadow-sm rounded-4 p-4 text-center text-muted border">
        No results available
      </div>
    );
  }

  return (
    <div className="ranking-container">
      {/* ================= PODIUM ================= */}
      <div
        className="d-flex align-items-end justify-content-center mb-5 mt-4 px-2"
        style={{ minHeight: "220px" }}
      >
        {podiumOrder.map((m) => {
          const isFirst = m.rank === 1;
          const isSecond = m.rank === 2;

          const medalColor = isFirst
            ? "#FFD700"
            : isSecond
              ? "#C0C0C0"
              : "#CD7F32";

          return (
            <div
              key={m.id}
              className="d-flex flex-column align-items-center position-relative mx-1 mx-md-3"
              onClick={() => navigate(`/u/${m.username || m.id}`)}
              style={{ cursor: "pointer", width: "30%" }}
            >
              {/* Avatar */}
              <div className="position-relative mb-2">
                <img
                  src={m.photoURL || "/img/rrc2.png"}
                  alt={m.name}
                  className="rounded-circle border border-3 shadow-sm"
                  style={{
                    width: isFirst ? "80px" : "65px",
                    height: isFirst ? "80px" : "65px",
                    objectFit: "cover",
                    borderColor: medalColor,
                  }}
                />

                {isFirst && (
                  <span
                    className="position-absolute"
                    style={{
                      top: "-22px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      fontSize: "1.8rem",
                    }}
                  >
                    👑
                  </span>
                )}

                {/* Rank badge */}
                <div
                  className="position-absolute bottom-0 end-0 badge rounded-circle d-flex align-items-center justify-content-center shadow"
                  style={{
                    width: 24,
                    height: 24,
                    fontSize: "0.7rem",
                    backgroundColor: medalColor,
                    color: "white",
                    border: "2px solid var(--bs-body-bg)",
                  }}
                >
                  {m.rank}
                </div>
              </div>

              {/* Name */}
              <div className="text-center mt-2 px-1">
                <div
                  className={`fw-bold text-truncate mb-1 ${isFirst ? "podium-first" : "podium-name"
                    }`}
                  style={{
                    fontSize: isFirst ? "1rem" : "0.85rem",
                    maxWidth: "110px",
                    letterSpacing: "-0.02em",
                  }}
                >
                  {m.displayName || m.name}
                </div>

                {/* W/L */}
                <div
                  className="d-flex align-items-center justify-content-center gap-1 mb-2 opacity-75"
                  style={{ fontSize: "0.7rem", fontWeight: 700 }}
                >
                  <span className="text-success">{m.wins}W</span>
                  <span className="text-muted">/</span>
                  <span className="text-danger">{m.losses}L</span>
                </div>

                {/* Points */}
                <div
                  className="d-inline-flex align-items-center justify-content-center px-2 py-1 rounded-pill shadow-sm"
                  style={{
                    backgroundColor: isFirst
                      ? THEME_COLOR
                      : "var(--bs-tertiary-bg)",
                    color: isFirst
                      ? "white"
                      : THEME_COLOR,
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    border: `1px solid ${THEME_COLOR}40`,
                  }}
                >
                  <i
                    className="bi bi-lightning-fill me-1"
                    style={{ fontSize: "0.6rem" }}
                  ></i>
                  {m.points}
                  <span
                    className="ms-1 opacity-75"
                    style={{ fontSize: "0.6rem" }}
                  >
                    PTS
                  </span>
                </div>

                {/* Accent Divider */}
                <div className="d-flex justify-content-center my-2">
                  <div
                    style={{
                      height: 3,
                      width: isFirst ? 60 : 40,
                      borderRadius: 2,
                      background: `linear-gradient(90deg, transparent 0%, ${medalColor} 50%, transparent 100%)`,
                    }}
                  />
                </div>
              </div>

              {/* Step */}
              <div
                className="w-100 rounded-top-3 d-flex align-items-center justify-content-center position-relative overflow-hidden"
                style={{
                  height: isFirst ? "100px" : isSecond ? "70px" : "50px",
                  background: isFirst
                    ? `linear-gradient(180deg, ${THEME_COLOR} 0%, #4C1D95 100%)`
                    : "linear-gradient(180deg, var(--bs-secondary-bg) 0%, var(--bs-tertiary-bg) 100%)",
                  color: isFirst ? "white" : "var(--bs-body-color)",
                  border: `1px solid ${isFirst ? THEME_COLOR : "var(--bs-border-color)"
                    }`,
                  boxShadow: isFirst
                    ? "0 10px 25px rgba(0,0,0,0.25)"
                    : "inset 0 1px 0 rgba(255,255,255,0.05)",
                }}
              >
                <span
                  className="fw-bold"
                  style={{
                    fontSize: "2rem",
                    opacity: isFirst ? 0.25 : 0.5,
                    letterSpacing: "-2px",
                  }}
                >
                  {m.rank}
                </span>

                {/* subtle top glow */}
                {!isFirst && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: "30%",
                      background:
                        "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, transparent 100%)",
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ================= TABLE ================= */}
      {others.length > 0 && (
        <div className="card shadow-sm rounded-4 overflow-hidden border">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0 bg-transparent">
              <thead className="bg-body-tertiary">
                <tr
                  style={{
                    color: THEME_COLOR,
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    letterSpacing: "0.5px",
                  }}
                >
                  <th className="ps-3 py-3" style={{ width: 70 }}>
                    RANK
                  </th>
                  <th>PLAYER</th>
                  <th className="text-center">W</th>
                  <th className="text-center">L</th>
                  <th className="text-end pe-3">PTS</th>
                </tr>
              </thead>
              <tbody>
                {others.map((m) => (
                  <tr
                    key={m.id}
                    className="border-bottom"
                    onClick={() =>
                      navigate(`/u/${m.username || m.id}`)
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <td
                      className="ps-3 fw-bold text-muted"
                      style={{ fontSize: "0.85rem" }}
                    >
                      {m.rank}
                    </td>

                    <td>
                      <div className="d-flex align-items-center">
                        <img
                          src={m.photoURL || "/img/rrc2.png"}
                          alt={m.displayName || m.name}
                          className="rounded-circle border shadow-sm"
                          style={{
                            width: 34,
                            height: 34,
                            objectFit: "cover",
                            flexShrink: 0,
                            borderColor:
                              "var(--bs-border-color)",
                          }}
                        />

                        <div className="ms-2 overflow-hidden">
                          <div
                            className="fw-bold text-body text-truncate"
                            style={{
                              fontSize: "0.85rem",
                              lineHeight: "1.1",
                            }}
                          >
                            {m.displayName || m.name}
                          </div>

                          {m.username && (
                            <div
                              className="text-muted text-truncate"
                              style={{
                                fontSize: "0.7rem",
                                marginTop: 1,
                              }}
                            >
                              @{m.username}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="text-center text-success small">
                      {m.wins}
                    </td>
                    <td className="text-center text-danger small">
                      {m.losses}
                    </td>
                    <td className="text-end pe-3 fw-bold small">
                      {m.points}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}